export const ENVIRONMENTAL_STATUS_WIDGET_TYPE = 'environmental-status';

export const ENVIRONMENTAL_STATUS_ICON_TYPES = [
  { value: 'auto', label: 'Auto' },
  { value: 'temperature', label: 'Temperature' },
  { value: 'humidity', label: 'Humidity' },
  { value: 'co2', label: 'CO2 Level' },
  { value: 'air', label: 'Air Quality' },
  { value: 'light', label: 'Light' },
  { value: 'power', label: 'Power' },
  { value: 'generic', label: 'Generic Sensor' },
];

export const ENVIRONMENTAL_STATUS_ACCENTS = {
  temperature: '#0891B2',
  humidity: '#38BDF8',
  co2: '#22C55E',
  air: '#3CC3DF',
  light: '#FBBF24',
  power: '#F97316',
  generic: '#5CA2FF',
};

const METRIC_PRESETS = {
  temperature: {
    label: 'Temperature',
    unit: '°C',
    defaultValue: 20.1,
    decimals: 1,
    iconType: 'temperature',
  },
  humidity: {
    label: 'Humidity',
    unit: '%',
    defaultValue: 40,
    decimals: 0,
    iconType: 'humidity',
  },
  co2: {
    label: 'CO2 Level',
    unit: 'ppm',
    defaultValue: 631,
    decimals: 0,
    iconType: 'co2',
  },
  air: {
    label: 'Air Quality',
    unit: '',
    defaultValue: 0,
    decimals: 0,
    iconType: 'air',
  },
  light: {
    label: 'Light',
    unit: 'lx',
    defaultValue: 0,
    decimals: 0,
    iconType: 'light',
  },
  power: {
    label: 'Power',
    unit: 'W',
    defaultValue: 0,
    decimals: 0,
    iconType: 'power',
  },
  generic: {
    label: 'Datastream',
    unit: '',
    defaultValue: 0,
    decimals: 0,
    iconType: 'generic',
  },
};

const createMetricId = (prefix = 'metric') =>
  `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

export const normalizeEnvironmentalIconType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(METRIC_PRESETS, normalized)
    ? normalized
    : 'generic';
};

export const inferEnvironmentalMetricKind = (...sources) => {
  const haystack = sources
    .map((source) => String(source || '').toLowerCase())
    .join(' ');

  if (
    haystack.includes('co2') ||
    haystack.includes('co₂') ||
    haystack.includes('carbon dioxide')
  ) {
    return 'co2';
  }

  if (
    haystack.includes('temp') ||
    haystack.includes('therm') ||
    haystack.includes('celsius') ||
    haystack.includes('fahrenheit')
  ) {
    return 'temperature';
  }

  if (
    haystack.includes('humid') ||
    haystack.includes('moisture') ||
    haystack.includes('water vapor')
  ) {
    return 'humidity';
  }

  if (haystack.includes('air') || haystack.includes('iaq') || haystack.includes('aqi')) {
    return 'air';
  }

  if (haystack.includes('light') || haystack.includes('lux') || haystack.includes('illum')) {
    return 'light';
  }

  if (
    haystack.includes('power') ||
    haystack.includes('energy') ||
    haystack.includes('current') ||
    haystack.includes('voltage') ||
    haystack.includes('watt')
  ) {
    return 'power';
  }

  return 'generic';
};

export const createEnvironmentalStatusMetric = (kind = 'generic', overrides = {}) => {
  const normalizedKind =
    kind === 'auto'
      ? inferEnvironmentalMetricKind(overrides?.label, overrides?.unit)
      : normalizeEnvironmentalIconType(kind);
  const preset = METRIC_PRESETS[normalizedKind] || METRIC_PRESETS.generic;
  const providedDecimals = Number(overrides?.decimals);

  return {
    id: overrides?.id || createMetricId(`environmental-${normalizedKind}`),
    label: overrides?.label ?? preset.label,
    deviceId: Number(overrides?.deviceId) || 0,
    dataId:
      overrides?.dataId === null || overrides?.dataId === undefined || overrides?.dataId === ''
        ? null
        : Number(overrides.dataId),
    pin: overrides?.pin || '',
    enabled: typeof overrides?.enabled === 'boolean' ? overrides.enabled : true,
    defaultValue:
      overrides?.defaultValue !== undefined ? overrides.defaultValue : preset.defaultValue,
    unit: overrides?.unit ?? preset.unit,
    decimals: Number.isFinite(providedDecimals) ? Math.max(0, providedDecimals) : preset.decimals,
    iconType: overrides?.iconType || preset.iconType,
    labelBackgroundColor: overrides?.labelBackgroundColor || '',
    labelBackgroundChangeColorBasedOnValue: overrides?.labelBackgroundChangeColorBasedOnValue === true,
    labelBackgroundColorGrading: overrides?.labelBackgroundColorGrading !== false,
    labelBackgroundColors: Array.isArray(overrides?.labelBackgroundColors) ? overrides.labelBackgroundColors : [],
    labelColor: overrides?.labelColor || '',
    labelChangeColorBasedOnValue: overrides?.labelChangeColorBasedOnValue === true,
    labelColorGrading: overrides?.labelColorGrading !== false,
    labelColors: Array.isArray(overrides?.labelColors) ? overrides.labelColors : [],
    valueColor: overrides?.valueColor || '',
    valueChangeColorBasedOnValue: overrides?.valueChangeColorBasedOnValue === true,
    valueColorGrading: overrides?.valueColorGrading !== false,
    valueColors: Array.isArray(overrides?.valueColors) ? overrides.valueColors : [],
    unitColor: overrides?.unitColor || '',
    unitChangeColorBasedOnValue: overrides?.unitChangeColorBasedOnValue === true,
    unitColorGrading: overrides?.unitColorGrading !== false,
    unitColors: Array.isArray(overrides?.unitColors) ? overrides.unitColors : [],
    iconBackgroundColor: overrides?.iconBackgroundColor || '',
    iconBackgroundChangeColorBasedOnValue: overrides?.iconBackgroundChangeColorBasedOnValue === true,
    iconBackgroundColorGrading: overrides?.iconBackgroundColorGrading !== false,
    iconBackgroundColors: Array.isArray(overrides?.iconBackgroundColors) ? overrides.iconBackgroundColors : [],
    iconColor: overrides?.iconColor || '',
    iconChangeColorBasedOnValue: overrides?.iconChangeColorBasedOnValue === true,
    iconColorGrading: overrides?.iconColorGrading !== false,
    iconColors: Array.isArray(overrides?.iconColors) ? overrides.iconColors : [],
  };
};

export const buildDefaultEnvironmentalStatusMetrics = () => ([
  createEnvironmentalStatusMetric('temperature', { id: 'environmental-temperature' }),
  createEnvironmentalStatusMetric('humidity', { id: 'environmental-humidity' }),
  createEnvironmentalStatusMetric('co2', { id: 'environmental-co2' }),
]);

export const createEmptyEnvironmentalStatusMetric = (index = 0) =>
  createEnvironmentalStatusMetric('generic', {
    label: `Datastream ${index + 1}`,
    id: createMetricId('environmental-custom'),
    defaultValue: '',
    iconType: 'auto',
  });

export const getEnvironmentalAccent = (iconType, fallback) =>
  ENVIRONMENTAL_STATUS_ACCENTS[normalizeEnvironmentalIconType(iconType)] ||
  fallback ||
  ENVIRONMENTAL_STATUS_ACCENTS.generic;
